import { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'pt' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  pt: {
    // Navbar
    'nav.home': 'Início',
    'nav.services': 'Serviços',
    'nav.portfolio': 'Portfólio',
    'nav.about': 'Sobre Nós',
    'nav.resources': 'Recursos',
    'nav.contact': 'Contato',
    
    // Hero
    'hero.badge': 'Inteligência Ambiental / Soluções Baseadas em Dados',
    'hero.title': 'Transformando Dados de Biodiversidade em Insights Acionáveis',
    'hero.description': 'Somos especializados na criação de aplicações de ponta que transformam dados ambientais e de biodiversidade complexos em inteligência clara e acionável para iniciativas de conservação e sustentabilidade.',
    'hero.cta.solutions': 'Explore Nossas Soluções',
    'hero.cta.cases': 'Ver Estudos de Caso',
    
    // Services
    'services.badge': 'Nossa Expertise',
    'services.title': 'Soluções Especializadas em Dados Ambientais',
    'services.description': 'Serviços abrangentes projetados para transformar a forma como você coleta, analisa e utiliza dados ambientais',
    'services.app.title': 'Desenvolvimento de Aplicações Personalizadas',
    'services.app.desc': 'Soluções de software sob medida para coleta, análise e visualização de dados ambientais, projetadas especificamente para suas necessidades de pesquisa.',
    'services.data.title': 'Sistemas de Gestão de Dados',
    'services.data.desc': 'Arquiteturas de banco de dados robustas para armazenar, organizar e gerenciar conjuntos de dados de biodiversidade e ambientais em grande escala.',
    'services.analytics.title': 'Análises Avançadas',
    'services.analytics.desc': 'Modelagem estatística e algoritmos de aprendizado de máquina para extrair padrões significativos de dados ecológicos complexos.',
    'services.gis.title': 'Integração GIS',
    'services.gis.desc': 'Soluções de sistemas de informação geográfica para análise espacial e mapeamento de dados ambientais e de distribuição de espécies.',
    'services.cloud.title': 'Infraestrutura em Nuvem',
    'services.cloud.desc': 'Plataformas escaláveis baseadas em nuvem para processamento de dados em tempo real, armazenamento e ambientes de pesquisa colaborativa.',
    'services.training.title': 'Treinamento e Suporte',
    'services.training.desc': 'Programas de treinamento abrangentes e suporte técnico contínuo para garantir que sua equipe maximize o valor de nossas soluções.',
    
    // Portfolio
    'portfolio.badge': 'Nosso Trabalho',
    'portfolio.title': 'Projetos Desenvolvidos',
    'portfolio.description': 'Aplicações interativas para análise de dados ambientais e da biodiversidade',
    'portfolio.project1.title': 'Análise de Acurácia',
    'portfolio.project1.client': 'Estado do Acre',
    'portfolio.project1.type': 'Análise de Dados',
    'portfolio.project1.desc': 'Análise de acurácia dos dados de atividade desmatamento e degradação florestal no estado do Acre.',
    'portfolio.project2.title': 'Espécies Exóticas Invasoras (IAS)',
    'portfolio.project2.client': 'Unidades de Conservação do Brasil',
    'portfolio.project2.type': 'Plataforma Web',
    'portfolio.project2.desc': 'Espécies Exóticas Invasoras em unidades de conservação (UCs) federais e estaduais do Brasil: status, distribuição patterns e recomendações para manejo.',
    'portfolio.project3.title': 'Hidrelétricas de SC',
    'portfolio.project3.client': 'Santa Catarina',
    'portfolio.project3.type': 'Data Visualization',
    'portfolio.project3.desc': 'Application for visualizing the distribution of hydroelectric plants across the state of Santa Catarina.',
    'portfolio.project4.title': 'AmeaçaR',
    'portfolio.project4.client': 'Southern Cone of Brazil',
    'portfolio.project4.type': 'Shiny Application',
    'portfolio.project4.desc': 'Search tool for endangered species in southern Brazil. Allows viewing lists of threatened flora and fauna by state (PR, SC, RS) and consulting information on conservation status.',
    'portfolio.project5.title': 'REDD+ Agroforestry Coffee',
    'portfolio.project5.client': 'IDESAM',
    'portfolio.project5.type': 'Monitoring System',
    'portfolio.project5.desc': 'Carbon emission reduction through avoided deforestation in native forest areas of partner families.',
    'portfolio.project6.title': 'splinkr',
    'portfolio.project6.client': 'speciesLink Network',
    'portfolio.project6.type': 'R Package',
    'portfolio.project6.desc': 'An R package that provides access to biological collection data through the speciesLink network.',
    'portfolio.project7.title': 'BD CPI-Acre (IUCN)',
    'portfolio.project7.client': 'CPI-Acre / IUCN',
    'portfolio.project7.type': 'Consulting',
    'portfolio.project7.desc': 'Specialized consulting aimed at "Technical and organizational strengthening of the Indigenous Agroforestry Agents (AAFI) initiative - CPI-Acre".',
    
    // About
    'about.badge': 'Sobre Nós',
    'about.title': 'Liderando o Caminho na Inovação de Dados Ambientais',
    'about.p1': 'Desde nossa fundação, estamos na vanguarda do desenvolvimento de soluções de software inovadoras que capacitam pesquisadores ambientais, organizações de conservação e profissionais de sustentabilidade a tomar decisões informadas baseadas em análise robusta de dados.',
    'about.p2': 'Nossa equipe de desenvolvedores especializados, cientistas de dados e especialistas ambientais trabalha colaborativamente para criar ferramentas que conectam dados ecológicos complexos a insights acionáveis. Somos apaixonados por usar tecnologia para proteger a biodiversidade e os recursos naturais do nosso planeta.',
    'about.stat1': 'Anos de Experiência',
    'about.stat2': 'Projetos Concluídos',
    'about.stat3': 'Parceiros Globais',
    'about.stat4': 'Pontos de Dados Analisados',
    'about.cta': 'Conheça Nossa Equipe',
    'about.quote': '"Nossa missão é transformar dados ambientais em ferramentas poderosas para conservação e sustentabilidade, criando um impacto positivo duradouro em nosso planeta."',
    
    // Resources
    'resources.badge': 'Centro de Conhecimento',
    'resources.title': 'Recursos e Insights',
    'resources.description': 'Acesse nossa biblioteca de pesquisas, guias e materiais educacionais sobre análise de dados ambientais',
    'resources.download': 'Baixar',
    'resources.item1.type': 'White Paper',
    'resources.item1.title': 'O Futuro da Gestão de Dados de Biodiversidade',
    'resources.item1.excerpt': 'Explore tendências emergentes e tecnologias que moldam como coletamos, analisamos e utilizamos dados de biodiversidade para esforços de conservação.',
    'resources.item2.type': 'Estudo de Caso',
    'resources.item2.title': 'Restaurando Recifes de Coral Através de Análise de Dados',
    'resources.item2.excerpt': 'Saiba como nossa plataforma ajudou biólogos marinhos a rastrear e restaurar ecossistemas de recifes de coral usando sistemas avançados de monitoramento.',
    'resources.item3.type': 'Webinar',
    'resources.item3.title': 'Aprendizado de Máquina para Identificação de Espécies',
    'resources.item3.excerpt': 'Junte-se aos nossos especialistas enquanto demonstram como IA e aprendizado de máquina estão revolucionando o monitoramento de vida selvagem e classificação de espécies.',
    'resources.item4.type': 'Guia',
    'resources.item4.title': 'Melhores Práticas para Coleta de Dados Ambientais',
    'resources.item4.excerpt': 'Um guia abrangente cobrindo metodologias, ferramentas e padrões para coletar dados ambientais de campo de alta qualidade.',
    'resources.item5.type': 'Relatório',
    'resources.item5.title': 'Tendências Globais de Biodiversidade 2024',
    'resources.item5.excerpt': 'Relatório anual analisando padrões mundiais de biodiversidade, ameaças e sucessos de conservação baseados em dados agregados.',
    'resources.item6.type': 'Tutorial',
    'resources.item6.title': 'Introdução ao GIS para Conservação',
    'resources.item6.excerpt': 'Tutorial passo a passo introduzindo sistemas de informação geográfica e suas aplicações no planejamento de conservação.',
    
    // Contact
    'contact.badge': 'Entre em Contato',
    'contact.title': 'Inicie Seu Projeto',
    'contact.description': 'Pronto para transformar seus dados ambientais em insights acionáveis? Vamos discutir como podemos ajudá-lo a alcançar seus objetivos de conservação.',
    'contact.form.name': 'Seu Nome',
    'contact.form.email': 'Endereço de Email',
    'contact.form.organization': 'Nome da Organização',
    'contact.form.projectType': 'Selecione o Tipo de Projeto',
    'contact.form.projectType.app': 'Desenvolvimento de Aplicação Personalizada',
    'contact.form.projectType.database': 'Sistema de Gestão de Dados',
    'contact.form.projectType.analytics': 'Análises Avançadas',
    'contact.form.projectType.gis': 'Integração GIS',
    'contact.form.projectType.cloud': 'Infraestrutura em Nuvem',
    'contact.form.projectType.training': 'Treinamento e Suporte',
    'contact.form.projectType.other': 'Outro',
    'contact.form.message': 'Conte-nos sobre seu projeto...',
    'contact.form.characters': 'caracteres',
    'contact.form.submit': 'Enviar Mensagem',
    'contact.form.sending': 'Enviando...',
    'contact.form.success': 'Obrigado! Entraremos em contato em breve.',
    'contact.form.error': 'Algo deu errado. Por favor, tente novamente.',
    'contact.info.title': 'Informações de Contato',
    'contact.info.location': 'Localização do Escritório',
    'contact.info.email': 'Endereço de Email',
    'contact.info.phone': 'Número de Telefone/WhatsApp',
    'contact.info.follow': 'Siga-nos',
    
    // Footer
    'footer.tagline': 'Transformando dados ambientais em insights acionáveis para um futuro sustentável.',
    'footer.solutions': 'Soluções',
    'footer.solutions.app': 'Desenvolvimento de Aplicações',
    'footer.solutions.data': 'Análise de Dados',
    'footer.solutions.consulting': 'Consultoria',
    'footer.solutions.training': 'Treinamento',
    'footer.resources': 'Recursos',
    'footer.resources.blog': 'Blog',
    'footer.resources.cases': 'Estudos de Caso',
    'footer.resources.docs': 'Documentação',
    'footer.resources.api': 'Acesso à API',
    'footer.newsletter.title': '',
    'footer.newsletter.desc': '',
    'footer.newsletter.placeholder': 'Seu email',
    'footer.newsletter.success': 'Inscrito com sucesso!',
    'footer.newsletter.error': 'Algo deu errado. Por favor, tente novamente.',
    'footer.copyright': '© 2024 EcoDataGeo Solutions. Todos os direitos reservados.',
    'footer.privacy': 'Política de Privacidade',
    'footer.terms': 'Termos de Serviço',

    // Publications
    'nav.publications': 'Publicações',
    'publications.badge': 'Produção Acadêmica',
    'publications.title': 'Publicações e Artigos',
    'publications.description': 'Artigos científicos publicados em periódicos, livros e relatórios técnicos sobre meio ambiente, biodiversidade e conservação',
    'publications.tab.articles': 'Artigos Científicos',
    'publications.tab.author': 'Livros (Autor)',
    'publications.tab.coauthor': 'Livros (Coautor)',
    'publications.type.article': 'Artigo',
    'publications.type.author': 'Autor',
    'publications.type.coauthor': 'Coautor',
    'publications.summary.articles': 'Artigos Publicados',
    'publications.summary.author': 'Livros como Autor',
    'publications.summary.coauthor': 'Livros como Coautor',
  },
  en: {
    // Navbar
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.portfolio': 'Portfolio',
    'nav.about': 'About Us',
    'nav.resources': 'Resources',
    'nav.contact': 'Contact',
    
    // Hero
    'hero.badge': 'Environmental Intelligence / Data-Driven Solutions',
    'hero.title': 'Transforming Biodiversity Data Into Actionable Insights',
    'hero.description': 'We specialize in creating cutting-edge applications that turn complex environmental and biodiversity data into clear, actionable intelligence for conservation and sustainability initiatives.',
    'hero.cta.solutions': 'Explore Our Solutions',
    'hero.cta.cases': 'View Case Studies',
    
    // Services
    'services.badge': 'Our Expertise',
    'services.title': 'Specialized Environmental Data Solutions',
    'services.description': 'Comprehensive services designed to transform how you collect, analyze, and utilize environmental data',
    'services.app.title': 'Custom Application Development',
    'services.app.desc': 'Tailored software solutions for environmental data collection, analysis, and visualization designed specifically for your research needs.',
    'services.data.title': 'Data Management Systems',
    'services.data.desc': 'Robust database architectures for storing, organizing, and managing large-scale biodiversity and environmental datasets.',
    'services.analytics.title': 'Advanced Analytics',
    'services.analytics.desc': 'Statistical modeling and machine learning algorithms to extract meaningful patterns from complex ecological data.',
    'services.gis.title': 'GIS Integration',
    'services.gis.desc': 'Geographic information system solutions for spatial analysis and mapping of environmental and species distribution data.',
    'services.cloud.title': 'Cloud Infrastructure',
    'services.cloud.desc': 'Scalable cloud-based platforms for real-time data processing, storage, and collaborative research environments.',
    'services.training.title': 'Training & Support',
    'services.training.desc': 'Comprehensive training programs and ongoing technical support to ensure your team maximizes the value of our solutions.',
    
    // Portfolio
    'portfolio.badge': 'Our Work',
    'portfolio.title': 'Developed Projects',
    'portfolio.description': 'Interactive applications for environmental and biodiversity data analysis',
    'portfolio.project1.title': 'Accuracy Analysis',
    'portfolio.project1.client': 'State of Acre',
    'portfolio.project1.type': 'Data Analysis',
    'portfolio.project1.desc': 'Accuracy analysis of deforestation and forest degradation activity data in the state of Acre.',
    'portfolio.project2.title': 'Invasive Exotic Species (IAS)',
    'portfolio.project2.client': 'Conservation Units of Brazil',
    'portfolio.project2.type': 'Web Platform',
    'portfolio.project2.desc': 'Invasive Exotic Species in federal and state conservation units (CUs) of Brazil: status, distribution patterns and management recommendations.',
    'portfolio.project3.title': 'Hydroelectric Plants of SC',
    'portfolio.project3.client': 'Santa Catarina',
    'portfolio.project3.type': 'Data Visualization',
    'portfolio.project3.desc': 'Application for visualizing the distribution of hydroelectric plants across the state of Santa Catarina.',
    'portfolio.project4.title': 'AmeaçaR',
    'portfolio.project4.client': 'Southern Cone of Brazil',
    'portfolio.project4.type': 'Shiny Application',
    'portfolio.project4.desc': 'Search tool for endangered species in southern Brazil. Allows viewing lists of threatened flora and fauna by state (PR, SC, RS) and consulting information on conservation status.',
    'portfolio.project5.title': 'REDD+ Agroforestry Coffee',
    'portfolio.project5.client': 'IDESAM',
    'portfolio.project5.type': 'Monitoring System',
    'portfolio.project5.desc': 'Carbon emission reduction through avoided deforestation in native forest areas of partner families.',
    'portfolio.project6.title': 'splinkr',
    'portfolio.project6.client': 'speciesLink Network',
    'portfolio.project6.type': 'R Package',
    'portfolio.project6.desc': 'An R package that provides access to biological collection data through the speciesLink network.',
    'portfolio.project7.title': 'BD CPI-Acre (IUCN)',
    'portfolio.project7.client': 'CPI-Acre / IUCN',
    'portfolio.project7.type': 'Consulting',
    'portfolio.project7.desc': 'Specialized consulting aimed at "Technical and organizational strengthening of the Indigenous Agroforestry Agents (AAFI) initiative - CPI-Acre".',
    
    // About
    'about.badge': 'About Us',
    'about.title': 'Leading the Way in Environmental Data Innovation',
    'about.p1': 'Since our founding, we have been at the forefront of developing innovative software solutions that empower environmental researchers, conservation organizations, and sustainability professionals to make informed decisions based on robust data analysis.',
    'about.p2': 'Our team of expert developers, data scientists, and environmental specialists work collaboratively to create tools that bridge the gap between complex ecological data and actionable insights. We are passionate about using technology to protect our planet\'s biodiversity and natural resources.',
    'about.stat1': 'Years of Experience',
    'about.stat2': 'Projects Completed',
    'about.stat3': 'Global Partners',
    'about.stat4': 'Data Points Analyzed',
    'about.cta': 'Meet Our Team',
    'about.quote': '"Our mission is to transform environmental data into powerful tools for conservation and sustainability, creating a lasting positive impact on our planet."',
    
    // Resources
    'resources.badge': 'Knowledge Hub',
    'resources.title': 'Resources & Insights',
    'resources.description': 'Access our library of research, guides, and educational materials on environmental data analysis',
    'resources.download': 'Download',
    'resources.item1.type': 'White Paper',
    'resources.item1.title': 'The Future of Biodiversity Data Management',
    'resources.item1.excerpt': 'Explore emerging trends and technologies shaping how we collect, analyze, and utilize biodiversity data for conservation efforts.',
    'resources.item2.type': 'Case Study',
    'resources.item2.title': 'Restoring Coral Reefs Through Data Analytics',
    'resources.item2.excerpt': 'Learn how our platform helped marine biologists track and restore coral reef ecosystems using advanced monitoring systems.',
    'resources.item3.type': 'Webinar',
    'resources.item3.title': 'Machine Learning for Species Identification',
    'resources.item3.excerpt': 'Join our experts as they demonstrate how AI and machine learning are revolutionizing wildlife monitoring and species classification.',
    'resources.item4.type': 'Guide',
    'resources.item4.title': 'Best Practices for Environmental Data Collection',
    'resources.item4.excerpt': 'A comprehensive guide covering methodologies, tools, and standards for collecting high-quality environmental field data.',
    'resources.item5.type': 'Report',
    'resources.item5.title': 'Global Biodiversity Trends 2024',
    'resources.item5.excerpt': 'Annual report analyzing worldwide biodiversity patterns, threats, and conservation successes based on aggregated data.',
    'resources.item6.type': 'Tutorial',
    'resources.item6.title': 'Getting Started with GIS for Conservation',
    'resources.item6.excerpt': 'Step-by-step tutorial introducing geographic information systems and their applications in conservation planning.',
    
    // Contact
    'contact.badge': 'Get In Touch',
    'contact.title': 'Start Your Project',
    'contact.description': 'Ready to transform your environmental data into actionable insights? Let\'s discuss how we can help you achieve your conservation goals.',
    'contact.form.name': 'Your Name',
    'contact.form.email': 'Email Address',
    'contact.form.organization': 'Organization Name',
    'contact.form.projectType': 'Select Project Type',
    'contact.form.projectType.app': 'Custom Application Development',
    'contact.form.projectType.database': 'Data Management System',
    'contact.form.projectType.analytics': 'Advanced Analytics',
    'contact.form.projectType.gis': 'GIS Integration',
    'contact.form.projectType.cloud': 'Cloud Infrastructure',
    'contact.form.projectType.training': 'Training & Support',
    'contact.form.projectType.other': 'Other',
    'contact.form.message': 'Tell us about your project...',
    'contact.form.characters': 'characters',
    'contact.form.submit': 'Send Message',
    'contact.form.sending': 'Sending...',
    'contact.form.success': 'Thank you! We\'ll get back to you soon.',
    'contact.form.error': 'Something went wrong. Please try again.',
    'contact.info.title': 'Contact Information',
    'contact.info.location': 'Office Location',
    'contact.info.email': 'Email Address',
    'contact.info.phone': 'Phone Number/WhatsApp',
    'contact.info.follow': 'Follow Us',
    
    // Footer
    'footer.tagline': 'Transforming environmental data into actionable insights for a sustainable future.',
    'footer.solutions': 'Solutions',
    'footer.solutions.app': 'Application Development',
    'footer.solutions.data': 'Data Analysis',
    'footer.solutions.consulting': 'Consulting',
    'footer.solutions.training': 'Training',
    'footer.resources': 'Resources',
    'footer.resources.blog': 'Blog',
    'footer.resources.cases': 'Case Studies',
    'footer.resources.docs': 'Documentation',
    'footer.resources.api': 'API Access',
    'footer.newsletter.title': 'Stay Informed',
    'footer.newsletter.desc': 'Subscribe to our newsletter for the latest insights and updates.',
    'footer.newsletter.placeholder': 'Your email',
    'footer.newsletter.success': 'Subscribed successfully!',
    'footer.newsletter.error': 'Something went wrong. Please try again.',
    'footer.copyright': '© 2024 EcoDataGeo Solutions. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',

    // Publications
    'nav.publications': 'Publications',
    'publications.badge': 'Academic Production',
    'publications.title': 'Publications & Articles',
    'publications.description': 'Scientific articles published in journals, books and technical reports on environment, biodiversity and conservation',
    'publications.tab.articles': 'Scientific Articles',
    'publications.tab.author': 'Books (Author)',
    'publications.tab.coauthor': 'Books (Co-author)',
    'publications.type.article': 'Article',
    'publications.type.author': 'Author',
    'publications.type.coauthor': 'Co-author',
    'publications.summary.articles': 'Published Articles',
    'publications.summary.author': 'Books as Author',
    'publications.summary.coauthor': 'Books as Co-author',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('pt');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['pt']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
